import { RotateCcw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { uploadErrorKey } from './imageSendErrors'

// The phase-A tray: images staff have picked but not yet sent. Every card
// shows its own state, so a failure is attributable to one image instead of
// collapsing the whole selection into a single error.
//
// Inline styles match the surrounding InboxPage conventions rather than
// introducing a styling dependency this page does not already have.
export default function ImageBatchPreview({ items, onRetry, onRemove, maxImages, compact = false }) {
  const { t } = useTranslation()
  if (!items?.length) return null

  const thumb = compact ? 52 : 64

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: compact ? '8px 12px' : '10px 16px',
        borderTop: '0.5px solid #e0e3f0',
        background: '#fafbfe',
        alignItems: 'flex-start',
      }}
    >
      {items.map((item) => {
        const isError = item.status === 'error'
        const isBusy = item.status === 'compressing' || item.status === 'uploading'
        // An invalid file has no preview to show and cannot be retried into
        // validity, so its card offers removal only.
        const canRetry = isError && item.errorKind !== 'invalid_type'
        const label = isError ? t(uploadErrorKey(item.errorKind)) : null

        return (
          <div key={item.localId} style={{ width: thumb, flexShrink: 0 }}>
            <div
              style={{
                position: 'relative',
                width: thumb,
                height: thumb,
                borderRadius: 8,
                overflow: 'hidden',
                background: '#eef0f6',
                border: isError ? '1.5px solid #ef4444' : item.status === 'uploaded' ? '1.5px solid #86efac' : '0.5px solid #e0e3f0',
              }}
              title={isError ? [label, item.errorDetail].filter(Boolean).join(' — ') : item.file?.name}
            >
              {item.previewUrl ? (
                <img
                  src={item.previewUrl}
                  alt={item.file?.name || ''}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: isError ? 0.45 : isBusy ? 0.6 : 1 }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#6b7280', textAlign: 'center', padding: 4 }}>
                  {t('inbox.batchError.invalidType')}
                </div>
              )}

              {isBusy && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.45)' }}>
                  <div
                    style={{ width: 16, height: 16, border: '2px solid #1535c8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}
                    aria-label={item.status === 'compressing' ? t('inbox.batchCompressing') : t('inbox.batchUploading')}
                  />
                </div>
              )}

              {item.status === 'uploaded' && (
                <div style={{ position: 'absolute', bottom: 2, right: 2, width: 15, height: 15, borderRadius: '50%', background: '#16a34a', color: 'white', fontSize: 10, lineHeight: '15px', textAlign: 'center' }}>
                  ✓
                </div>
              )}

              <button
                type="button"
                onClick={() => onRemove(item.localId)}
                title={t('inbox.batchRemove')}
                aria-label={t('inbox.batchRemove')}
                style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, borderRadius: '50%', border: 'none', background: 'rgba(17,17,17,0.6)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
              >
                <X size={10} />
              </button>
            </div>

            {isError && (
              <div style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 9, color: '#ef4444', lineHeight: 1.2, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {label}
                </span>
                {canRetry && (
                  <button
                    type="button"
                    onClick={() => onRetry(item.localId)}
                    title={t('inbox.batchRetry')}
                    aria-label={t('inbox.batchRetry')}
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: '#1535c8', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                  >
                    <RotateCcw size={10} />
                  </button>
                )}
              </div>
            )}
          </div>
        )
      })}

      <span style={{ fontSize: 10, color: '#9ca3af', alignSelf: 'center', marginLeft: 'auto' }}>
        {items.length}/{maxImages}
      </span>
    </div>
  )
}
